import { useCallback, useEffect, useState } from "react";
import UserLoginSwitch from "../auth/UserLoginSwitch";
import {
  generateAbbreviation,
  getLoggedinUser,
  useCartPaymentCharge,
  useGetProduct,
  useUpdateCart,
} from "../../lib/util";
import { useFormatCurrency } from "../common/format-currency";
import useDebounce from "../common/useDebounce";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import { cartIcon } from "../../assets";
import { updateCart } from "../slice/cart-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import PaymentMethod from "./payment-method";
import { useHistoryManager } from "../history/useHistoryManager";
import { getActiveUser } from "../slice/auth-slice";
import { store } from "../slice/store";
import Spinner from "../common/Spinner";

const calculateTotal = (cartItems) => {
  if (!cartItems.paymentItem) {
    return 0;
  }
  return cartItems.paymentItem.reduce(
    (acc, product) => acc + product.amount * product.unit,
    0,
  );
};

const CartItem = ({
  item,
  accessPointId,
  handleControlClick,
  showProduct,
  reportInvalidItem,
  showControls,
  disableStoreProductPrice,
  storePath,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const [product, setProduct] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const { isLoading, mutate: mututateGetProduct } = useGetProduct({
    onError: (response) => {
      setError(response.message);
    },
    onSuccess: async (response) => {
      setProduct(response.data.product);
    },
  });

  useEffect(() => {
    if (item && item.productId && !product) {
      mututateGetProduct({
        productId: item.productId,
        accessPointId,
      });
    }
  }, [item.productId, product]);

  useEffect(() => {
    if (product) {
      const isOutOfStock = !product.unlimited && product.sku <= 0;
      const isAtMax = !product.unlimited && item.unit > product.sku;

      const isInvalid = isOutOfStock || isAtMax;
      reportInvalidItem(item.productId, isInvalid);
    }
  }, [product, item.unit]);

  const viewProduct = () => {
    if (storePath === "market-place") {
      return;
    }
    if (product) {
      showProduct(product);
    }
  };

  const isOutOfStock = !product?.unlimited && product?.sku <= 0;
  const isAtMax = !product?.unlimited && item.unit > product?.sku;
  return (
    <>
      <div key={item._id} className="cart-item">
        <div className="cart-box">
          <div className="cart-item-header">
            <div className="variant-display">
              <h3 onClick={() => viewProduct()}>
                {item.name}
                {!showControls && (
                  <>
                    {" X "}
                    {item.unit}{" "}
                  </>
                )}
              </h3>
              {item.isVariant && item.variant && item.node === "CHILD" && (
                <div className="variant-item">
                  <span className="variant-info">
                    {item.variant.hasColour && (
                      <span>Color: {item.variant.colorName}</span>
                    )}
                    {item.variant.hasColour && item.variant.hasSize && (
                      <span> &nbsp;|&nbsp; </span>
                    )}
                    {item.variant.hasSize && (
                      <span>Size: {item.variant.size}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="cart-item-content">
            {/* {item.mediaThumb && (
              <div className="cart-info-img" onClick={() => viewProduct()}>
                {!imageLoaded && <Skeleton height={80} width={80} />}
                <ImageComponent
                  alt={item.name}
                  src={item.mediaThumb.replace("r200x200", "r300x300")}
                  className={styles.imageCard}
                  onLoad={() => setImageLoaded(true)}
                  style={imageLoaded ? {} : { display: "none" }}
                />
              </div>
            )} */}
            <div className="cart-info-img" onClick={() => viewProduct()}>
              {!item.mediaThumb ? (
                // No image available — abbreviation fallback
                <div
                  className="abbrevated-div"
                  style={{
                    width: 85,
                    height: 85,
                    fontSize: "2.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f0f0f0",
                    borderRadius: "0.25rem",
                    marginRight: "0.5rem",
                    flexShrink: 0,
                  }}
                  title={item.name}
                >
                  {generateAbbreviation(item.name)}
                </div>
              ) : (
                // Image available — skeleton until loaded
                <div
                  style={{
                    width: 85,
                    height: 85,
                    position: "relative",
                    marginRight: "0.5rem",
                    flexShrink: 0,
                  }}
                >
                  {!imageLoaded && <Skeleton width={85} height={85} />}
                  {imageLoaded && (
                    <img
                      src={item.mediaThumb.replace("r200x200", "r300x300")}
                      alt={item.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "0.25rem",
                        display: "block",
                      }}
                    />
                  )}
                  {/* Always load image to trigger onLoad */}
                  <img
                    src={item.mediaThumb.replace("r200x200", "r300x300")}
                    alt={item.name}
                    style={{ display: "none" }}
                    onLoad={() => setImageLoaded(true)}
                  />
                </div>
              )}
            </div>

            <div className="cart-items-info">
              <div className="cart-btns">
                <div className="cart-info-case">
                  {showControls && (
                    <div className="quantity-controls">
                      <button
                        onClick={() =>
                          handleControlClick("decrease", item._id, product)
                        }
                        disabled={item.unit <= 1 || isLoading}
                      >
                        -
                      </button>

                      <span
                        title={
                          isAtMax || isOutOfStock
                            ? "No more stock available"
                            : ""
                        }
                        className={
                          isAtMax || isOutOfStock ? "quantity-alert" : ""
                        }
                      >
                        {" "}
                        {item.unit}{" "}
                      </span>

                      <button
                        onClick={() =>
                          handleControlClick("increase", item._id, product)
                        }
                        disabled={
                          (!product?.unlimited && product?.sku <= item.unit) ||
                          isLoading
                        }
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
                {showControls && (
                  <div className="cart-remove-box">
                    <button
                      onClick={() =>
                        handleControlClick("remove", item._id, product)
                      }
                      className="cart-item-remove"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div className="cart-details-info">
                <p className="cart-price">
                  {disableStoreProductPrice
                    ? "Contact Seller for Price"
                    : ` Price: ${formatCurrency(item.amount, item.currency)}`}
                </p>

                {isOutOfStock && (
                  <div className="out-of-stock-warning">
                    This product is currently out of stock.
                  </div>
                )}

                {!isOutOfStock && isAtMax && (
                  <div className="cart-warning-text">
                    You&#39;ve exceeded the maximum available quantity.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CartSummary = ({
  cart,
  onProceed,
  accessPoint,
  setError,
  gotoCart,
  gotoDelivery,
  gotoContact,
  disableStoreProductPrice,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const [paymentChargeData, setPaymentChargeData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("ONLINE");

  useEffect(() => {
    if (cart && paymentMethod == "ONLINE") {
      const orderData = {
        collectionId: cart.collectionId,
        mode: "ONLINE",
        gateWay: "PAYSTACK",
        channel: "WEB",
        amount: cart.totalAmount,
      };
      mutateInitializePaymentCharge(orderData);
    }
  }, [paymentMethod, cart?.collectionId, cart?.totalAmount]);

  const { isLoading: isLoadingCharge, mutate: mutateInitializePaymentCharge } =
    useCartPaymentCharge({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        setPaymentChargeData(response.data);
      },
    });

  // Fallbacks and mappings
  const contact = cart.publicOrder ? cart.publicInfo : cart.author;
  const currency = cart.currency;

  if (!cart || !cart.paymentItem || cart.paymentItem.length === 0) {
    return (
      <div className="cart-summary">
        <div className="empty-cart-message">Your cart is empty.</div>
      </div>
    );
  }

  const gatewayFee =
    paymentChargeData &&
    paymentChargeData.transactionChargeBearer === "USER" &&
    paymentMethod === "ONLINE"
      ? (paymentChargeData.processingFee || 0) +
        (paymentChargeData.addTransactionCharge
          ? paymentChargeData.toCharge || 0
          : 0)
      : 0;

  const EditHeader = ({ label, onEdit }) => (
    <div className="summary-header">
      <h3>{label}</h3>
      <span className="summary-header-edit" onClick={onEdit}>
        Edit
      </span>
    </div>
  );

  return (
    <div className="cart-summary">
      <h2>
        Order Summary
        {cart.checkSum && (
          <span
            style={{
              fontWeight: "normal",
              fontSize: "0.9em",
              marginLeft: "10px",
            }}
          >
            (Order No: {cart.checkSum})
          </span>
        )}
      </h2>

      {/* <hr /> */}

      {/* Contact Info */}

      <EditHeader label="Contact Information:" onEdit={gotoContact} />
      <div className="summary-line">
        <span>Name:</span>
        <span>
          {contact?.name?.trim() ||
            [contact?.firstName, contact?.lastName].filter(Boolean).join(" ") ||
            "N/A"}
        </span>
      </div>
      <div className="summary-line">
        <span>Phone:</span>
        {contact.phone ? (
          <span>
            {contact.intCode
              ? `${contact.intCode} ${contact.phone}`
              : contact.phone}
          </span>
        ) : (
          <span>N/A</span>
        )}
      </div>
      <div className="summary-line">
        <span>Email:</span>
        <span>{contact?.email || "N/A"}</span>
      </div>

      {/* Delivery Information */}
      {cart.deliveryInfo && cart.deliveryMode !== "INSTORE" && (
        <>
          <hr />

          <EditHeader
            label={
              cart.deliveryMode === "PICKUP"
                ? "Pickup Address"
                : "Delivery Information"
            }
            onEdit={gotoDelivery}
          />

          {cart.deliveryMode === "LOCATION" ? (
            <>
              <div className="summary-line">
                <span>Address:</span>
                <span>{cart.deliveryInfo?.deliveryAddress || "N/A"}</span>
              </div>
              <div className="summary-line">
                <span>Region:</span>
                <span>{cart.deliveryInfo.deliveryRegion}</span>
              </div>
              <div className="summary-line">
                <span>Phone:</span>
                <span>
                  {cart.deliveryInfo?.intCode
                    ? `${cart.deliveryInfo.intCode} ${cart.deliveryInfo.phone}`
                    : cart.deliveryInfo?.phone || "N/A"}
                </span>
              </div>
              {cart.deliveryInfo.note && (
                <div className="summary-line">
                  <span>Note:</span>
                  <span>{cart.deliveryInfo.note}</span>
                </div>
              )}
            </>
          ) : (
            <div className="summary-line">
              <span>Pickup Address:</span>
              <span>{cart.deliveryInfo.pickUpAddress}</span>
            </div>
          )}
        </>
      )}

      {cart.deliveryMode === "INSTORE" && (
        <>
          <hr />
          <EditHeader label="Collection Mode" onEdit={gotoDelivery} />
          <div className="summary-line">
            <span>Mode:</span>
            <span>In-store Purchase</span>
          </div>
        </>
      )}

      <>
        <hr />
        <h3>Billing Summary</h3>
      </>

      {!disableStoreProductPrice && (
        <>
          {isLoadingCharge && (
            <div className="summary-line loading-line">
              <span>Loading Prosessing fees...</span>
              <span className="only-spinner" />
            </div>
          )}

          <div className="summary-line">
            <span>Subtotal:</span>
            <span>{formatCurrency(cart.amount, currency)}</span>
          </div>

          {cart.vatAmount > 0 && (
            <div className="summary-line">
              <span>
                VAT ({cart.vatPercentage}%
                {cart.vatIncludedInProductPrice ? ", Included in Product" : ""}
                ):
              </span>
              <span>{formatCurrency(cart.vatAmount, currency)}</span>
            </div>
          )}

          {cart.discountAmount > 0 && (
            <div className="summary-line">
              <span>Discount:</span>
              <span>
                -{formatCurrency(cart.discountAmount, currency)}
                {cart.discountPercentage
                  ? ` (${cart.discountPercentage}%)`
                  : ""}
              </span>
            </div>
          )}

          {cart.deliveryAmount > 0 && (
            <div className="summary-line">
              <span>Delivery Fee:</span>
              <span>{formatCurrency(cart.deliveryAmount, currency)}</span>
            </div>
          )}

          {gatewayFee > 0 && (
            <div className="summary-line">
              <span>Payment Prosessing Fee:</span>
              <span>
                {formatCurrency(
                  gatewayFee,
                  paymentChargeData.currency || currency,
                )}
              </span>
            </div>
          )}
        </>
      )}

      <hr />

      <div className="summary-line summary-total">
        <strong>Total:</strong>
        {disableStoreProductPrice ? (
          <div className="negotiable-message">
            <strong>Price to Be Confirmed</strong>
            <p className="small-text">
              You can place your order, seller will contact you to finalize the
              price.
            </p>
          </div>
        ) : (
          <strong>
            {formatCurrency(
              (cart?.totalAmount ?? 0) + (gatewayFee ?? 0),
              currency,
            )}
          </strong>
        )}
      </div>

      <div className="payment-method-section">
        <PaymentMethod
          cart={cart}
          accessPoint={accessPoint}
          onProceed={onProceed}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          gotoCart={gotoCart}
          disableStoreProductPrice={disableStoreProductPrice}
        />
      </div>
    </div>
  );
};

const CartCheckout = ({
  cart,
  error,
  setError,
  handleLoginClick,
  handleCartSubmit,
  showProduct,
  modal,
  closeModal,
  cartProcessLoading,
  activateBackButton,
  confirmation,
  onProceed,
  accessPoint,
  gotoCart,
  gotoDelivery,
  gotoContact,
  isPageMode,
  storePath,
  organisation,
  navLocation,
  invalidateCollections,
  setPopUp,
  startSwitch,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const disableStoreProductPrice =
    accessPoint?.settings?.disableStoreFrontPrice || false;

  const dispatch = useAppDispatch();
  useEffect(() => {
    activateBackButton(false);
  }, []);

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser()),
  );

  const [modify, setModify] = useState(false);

  const [cartItems, setCartItems] = useState({
    ...cart,
    paymentItem: cart?.paymentItem ?? [],
  });

  const { navigateTo } = useHistoryManager(storePath);

  const debouncedCartItems = useDebounce(cartItems, 1000); // Debounce with 500ms delay

  const [invalidItems, setInvalidItems] = useState([]);

  // const [showEmptyCartMessage, setShowEmptyCartMessage] = useState(false);

  // useEffect(() => {
  //   let timer;

  //   const isCartEmpty =
  //     !cartItems ||
  //     !cartItems.paymentItem ||
  //     cartItems.paymentItem.length === 0;

  //   if (isCartEmpty) {
  //     setShowEmptyCartMessage(false);
  //     timer = setTimeout(() => {
  //       setShowEmptyCartMessage(true);
  //     }, 3000);
  //   } else {
  //     setShowEmptyCartMessage(false);
  //   }

  //   return () => {
  //     if (timer) clearTimeout(timer);
  //   };
  // }, [cartItems]);

  useEffect(() => {
    if (cart) {
      setCartItems(cart);
    }
  }, [cart?.collectionId]);

  useEffect(() => {
    if (modify) {
      setModify(false);
      mututateUpdateCart({
        orgId: cartItems?.profileId,
        accessPointId: cartItems?.accessPointId,
        paymentItem: debouncedCartItems.paymentItem,
      });
    }
  }, [debouncedCartItems]);

  const { isLoading, mutate: mututateUpdateCart } = useUpdateCart({
    onError: (response) => {
      setError(response.message);
      const cart = response.data.collection;
      if (cart) {
        dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
      }
      invalidateCollections();
    },
    onSuccess: async (response) => {
      const cart = response.data.collection;
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
      setCartItems(cart);
      toast.success("Cart Updated");
      invalidateCollections();
    },
  });

  const handleControlClick = useCallback((action, itemId, product) => {
    if (!product) return;
    setModify(true);
    setError("");
    switch (action) {
      case "increase":
        increaseQuantity(itemId, product);
        break;
      case "decrease":
        decreaseQuantity(itemId);
        break;
      case "remove":
        removeItem(itemId, product);
        break;
      default:
        break;
    }
  }, []);

  const increaseQuantity = (itemId, product) => {
    if (!product.unlimited && product.sku <= 0) {
      toast.error(`${product.name} is out of stock.`);
      return;
    }
    setCartItems((prevItems) => {
      return {
        ...prevItems,
        paymentItem: prevItems.paymentItem.map((item) =>
          item._id === itemId && (product.unlimited || item.unit < product.sku)
            ? { ...item, unit: item.unit + 1 }
            : item,
        ),
      };
    });
  };

  const decreaseQuantity = (itemId) => {
    setCartItems((prevItems) => {
      return {
        ...prevItems,
        paymentItem: prevItems.paymentItem.map((item) =>
          item._id === itemId && item.unit > 1
            ? { ...item, unit: item.unit - 1 }
            : item,
        ),
      };
    });
  };

  const removeItem = (itemId, product) => {
    setCartItems((prevItems) => {
      const updatedItems = prevItems.paymentItem.filter(
        (item) => item._id !== itemId,
      );

      // Remove from invalidItems using productId
      if (product?.productId) {
        setInvalidItems((prev) =>
          prev.filter((id) => id !== product.productId),
        );
      }

      return {
        ...prevItems,
        paymentItem: updatedItems,
      };
    });
  };

  const reportInvalidItem = (productId, isInvalid) => {
    setInvalidItems((prev) => {
      const alreadyInvalid = prev.includes(productId);
      if (isInvalid && !alreadyInvalid) return [...prev, productId];
      if (!isInvalid && alreadyInvalid)
        return prev.filter((id) => id !== productId);
      return prev;
    });
  };

  const currency = cartItems.currency;

  return (
    <div>
      <div
        className={`cart-header${
          navLocation === "marketplace" ? " marketplace" : ""
        }`}
      >
        {cart && (
          <div>
            <h1>Cart Items from {cart.accessPointName}</h1>
          </div>
        )}
        {!isPageMode && !loggedInUser?.user && (
          <UserLoginSwitch handleLoginClick={handleLoginClick} />
        )}
      </div>
      {/* <div className="cart-divider" /> */}

      {(cartItems.paymentItem ?? []).length > 0 ? (
        <>
          <div className="cart-box show-both">
            <div className="cart-left-section">
              <div className="cart-items-container">
                {cartItems.paymentItem.map((item) => (
                  <CartItem
                    key={item._id}
                    item={item}
                    accessPointId={cart.accessPointId}
                    handleControlClick={handleControlClick}
                    showProduct={showProduct}
                    isPageMode={isPageMode}
                    reportInvalidItem={reportInvalidItem}
                    showControls={!confirmation}
                    disableStoreProductPrice={disableStoreProductPrice}
                    setPopUp={setPopUp}
                    orgId={organisation?.orgId}
                    profileId={cartItems.host?.profileId}
                    storePath={storePath}
                  />
                ))}
              </div>
              {error && <div className="error-message">{error}</div>}
              {invalidItems.length > 0 && (
                <div className="error-message">
                  Some items in your cart are out of stock or exceeded available
                  quantity
                </div>
              )}

              {!confirmation && (
                <>
                  {!disableStoreProductPrice && (
                    <div className="cart-total">
                      <h2>
                        Total:{" "}
                        {formatCurrency(calculateTotal(cartItems), currency)}
                      </h2>
                    </div>
                  )}
                  {storePath !== "market-place" && (
                    <div className="checkout-box">
                      <button
                        className="checkout-button"
                        onClick={handleCartSubmit}
                        disabled={cartProcessLoading || invalidItems.length > 0}
                      >
                        {cartProcessLoading ? "Checkin Out..." : "Checkout"}
                      </button>
                      {!isPageMode ? (
                        <button
                          className="checkout-secondary-button"
                          onClick={() =>
                            modal ? closeModal("continue") : navigateTo("home")
                          }
                        >
                          Continue shopping
                        </button>
                      ) : (
                        <button
                          className="checkout-secondary-button"
                          onClick={() => navigateTo(storePath)}
                        >
                          Continue shopping
                        </button>
                      )}
                    </div>
                  )}
                  {storePath === "market-place" && (
                    <div className="checkout-box">
                      <button
                        className="checkout-button"
                        cl
                        onClick={() => {
                     
                          startSwitch(cart);
                        }}
                        // disabled={cartProcessLoading || invalidItems.length > 0}
                      >
                        Checkout from {cart.host?.name}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {confirmation && (
              <>
                <div className="cart-right-section">
                  <CartSummary
                    cart={cartItems}
                    onProceed={onProceed}
                    accessPoint={accessPoint}
                    setError={setError}
                    gotoCart={gotoCart}
                    gotoDelivery={gotoDelivery}
                    gotoContact={gotoContact}
                    disableStoreProductPrice={disableStoreProductPrice}
                  />
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        // <>
        //   {!showEmptyCartMessage ? (
        //     <div
        //       style={{
        //         display: "flex",
        //         alignItems: "center",
        //         justifyContent: "center",
        //         width: "100%",
        //         height: "30rem",
        //       }}
        //     >
        //       <Spinner />
        //     </div>
        //   ) : (
        // <div className="cart-summary">
        <div className="empty-cart-message">
          <span>{cartIcon}</span>
          <h3>Your cart is empty.</h3>
          <p>Looks like you haven&apos;t added anything to your cart yet.</p>
          <button
            className="checkout-secondary-button"
            onClick={() =>
              modal ? closeModal("continue") : navigateTo("home")
            }
          >
            Continue shopping
          </button>
        </div>
        // </div>
      )}
      {/* </>
      )} */}
    </div>
  );
};

export default CartCheckout;
